import { FileService } from '@/services/file/fileService';

/**
 * Hook for file operations
 * Provides a clean interface for file-related operations without direct service access
 * Note: This hook does not handle UI feedback - components should handle toast notifications
 */
export const useFile = () => {
  /**
   * Fetch a file from a URL and return it as a Blob
   * @param url - The URL of the file to fetch
   * @returns Promise<Blob> - The file as a Blob
   * @throws Error if the fetch fails or response is not ok
   */
  const getFileBlob = async (url: string): Promise<Blob> => {
    return await FileService.getFileBlob(url);
  };

  /**
   * Download a file from a URL
   * @param url - The URL of the file to download
   * @param filename - The filename for the download (optional, defaults to extracted filename or 'document')
   * @returns Promise<string> - The filename that was downloaded
   * @throws Error if the download fails
   */
  const downloadFile = async (url: string, filename?: string): Promise<string> => {
    const finalFilename =
      filename || url.split('/').pop()?.split('?')[0] || 'document';
    const { downloadUrlAsDocument, openUrlInBrowser } = await import(
      '@/lib/native/downloadUrl'
    );
    try {
      await downloadUrlAsDocument(url, finalFilename);
      return finalFilename;
    } catch (primaryError) {
      try {
        await openUrlInBrowser(url);
        return finalFilename;
      } catch {
        throw primaryError;
      }
    }
  };

  return {
    getFileBlob,
    downloadFile,
  };
};

